import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import type { FileRejection, FileWithPath } from '@mantine/dropzone';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { z } from 'zod';
import { CameraCapture } from '../components/CameraCapture.js';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { ApiUnreachableError } from '../api/client.js';
import { OfflineQueueNotice } from '../components/OfflineQueueNotice.js';
import { translating } from '../i18n/resolver.js';
import { useT } from '../i18n/useT.js';
import type { MessageKey } from '../i18n/da.js';
import { fileToBase64, formatBytes } from '../files.js';
import { queueAttachment } from '../offline/enqueue.js';
import { formatDate } from '../format.js';
import {
  useOpenAttachment,
  useAttachments,
  useDeleteAttachment,
  useJournalEntries,
  useLinkAttachment,
  useUploadAttachment,
} from '../api/hooks.js';
import type { Attachment, OpenedAttachment } from '../api/hooks.js';

/**
 * Bilag — the documents behind the postings.
 *
 * The page is built around one asymmetry: **uploading is reversible and linking
 * is not**. An unlinked attachment is a file somebody picked by mistake and can
 * be deleted. The moment it documents a journal entry it becomes part of a
 * record the Bookkeeping Act requires to be kept for five years, the database
 * refuses to delete it, and there is no unlink.
 *
 * So the screen says that before the link is made, not after it is refused, and
 * the delete button is offered only where deleting is actually possible.
 */

/**
 * What may be attached.
 *
 * Mirrors `ALLOWED_MIME_TYPES` in packages/services/src/attachment.ts. This copy
 * is an **affordance**, not the rule: it filters the file picker and rejects a
 * bad drop without a round trip. The service still checks every upload, and it
 * is the authority — `image/svg+xml` is absent from both, because an SVG served
 * back from storage is a script running against whoever opens it.
 */
const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/heic',
  'text/plain',
  'text/csv',
  'application/xml',
  'text/xml',
];

/** 25 MB, matching MAX_ATTACHMENT_BYTES in the service. */
const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

const STATE_LABELS: Record<Attachment['state'], MessageKey> = {
  unlinked: 'att.state.unlinked',
  draft_linked: 'att.state.draftLinked',
  linked: 'att.state.linked',
};

const STATE_COLORS: Record<Attachment['state'], string> = {
  unlinked: 'yellow',
  draft_linked: 'gray',
  linked: 'green',
};

export function AttachmentsPage(): ReactNode {
  const t = useT();
  const [state, setState] = useState<string>('alle');
  const [linking, setLinking] = useState<Attachment | null>(null);
  const [deleting, setDeleting] = useState<Attachment | null>(null);
  const [linkOpened, linkModal] = useDisclosure(false);
  const [deleteOpened, deleteModal] = useDisclosure(false);

  const attachments = useAttachments(
    state === 'alle' ? {} : { state: state as Attachment['state'] },
  );

  return (
    <Stack gap="lg">
      <Title order={2}>{t('att.title')}</Title>

      <OfflineQueueNotice />

      <UploadPanel />

      <Group align="flex-end" wrap="wrap">
        <SegmentedControl
          value={state}
          onChange={setState}
          aria-label={t('att.filterLabel')}
          data={[
            { value: 'alle', label: t('att.filter.all') },
            { value: 'unlinked', label: t('att.state.unlinked') },
            { value: 'linked', label: t('att.state.linked') },
          ]}
        />
      </Group>

      {attachments.isPending && (
        <Center p="xl">
          <Loader />
        </Center>
      )}

      {attachments.isError && <ErrorMessage error={attachments.error} />}

      {attachments.isSuccess && attachments.data.items.length === 0 && (
        <Text c="dimmed">{t('att.none')}</Text>
      )}

      {attachments.isSuccess && attachments.data.items.length > 0 && (
        <Table.ScrollContainer minWidth={720}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('att.col.filename')}</Table.Th>
                <Table.Th style={{ width: 150 }}>{t('att.col.state')}</Table.Th>
                <Table.Th style={{ width: 110 }}>{t('att.col.size')}</Table.Th>
                <Table.Th style={{ width: 120 }}>{t('att.col.uploaded')}</Table.Th>
                <Table.Th style={{ width: 240 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {attachments.data.items.map((attachment) => (
                <AttachmentRow
                  key={attachment.id}
                  attachment={attachment}
                  onLink={() => {
                    setLinking(attachment);
                    linkModal.open();
                  }}
                  onDelete={() => {
                    setDeleting(attachment);
                    deleteModal.open();
                  }}
                />
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <LinkAttachmentModal
        attachment={linking}
        opened={linkOpened}
        onClose={() => {
          linkModal.close();
          setLinking(null);
        }}
      />
      <DeleteAttachmentModal
        attachment={deleting}
        opened={deleteOpened}
        onClose={() => {
          deleteModal.close();
          setDeleting(null);
        }}
      />
    </Stack>
  );
}

// -----------------------------------------------------------------------------
// Upload
// -----------------------------------------------------------------------------

/**
 * Getting a document in.
 *
 * Two affordances, because they serve two situations. The dropzone is the desk:
 * a PDF that arrived by email, dragged from a folder — and it stays clickable
 * and keyboard-reachable, so it is not a drag-only target. The camera button is
 * the other half of the job and the one genuinely mobile-first requirement in
 * this task: a receipt exists on paper and is photographed where it is handed
 * over.
 *
 * `capture="environment"` is on its own input rather than on the dropzone's.
 * Putting it there would make every file choice open the camera, including on a
 * laptop, where there is nothing to photograph.
 */
function UploadPanel(): ReactNode {
  const t = useT();
  const upload = useUploadAttachment();
  const [rejected, setRejected] = useState<string | null>(null);
  const [queuedCount, setQueuedCount] = useState(0);
  /*
    The "Fotografér bilag" home-screen shortcut used to land here with
    ?kamera=1. It now opens the viewfinder in the installed app (/app), where
    the camera is the whole screen — see CaptureActions.
  */
  const [cameraOpen, camera] = useDisclosure(false);

  async function send(files: readonly File[]): Promise<void> {
    setRejected(null);
    // Sequentially, not in parallel: each upload writes bytes to object storage
    // and then a row, and a browser firing ten of those at once is a burst the
    // API has no reason to absorb.
    for (const file of files) {
      const contentBase64 = await fileToBase64(file);

      /*
        Offline is the expected case for a photographed receipt, not a failure.

        A receipt is photographed where it is handed over, which is very often
        somewhere with no signal. Losing it there would be the worst outcome in
        this whole application: the paper is usually thrown away immediately
        afterwards, and the photograph is then the only evidence that ever
        existed of what was bought.
      */
      if (!navigator.onLine) {
        await queueAttachment({
          filename: file.name,
          mimeType: file.type,
          contentBase64,
        });
        setQueuedCount((count) => count + 1);
        continue;
      }

      try {
        const attachment = await upload.mutateAsync({
          filename: file.name,
          // A file the browser could not type is sent as an empty string rather
          // than a guess. The service decides, and its refusal names the
          // accepted types.
          mimeType: file.type,
          contentBase64,
        });
        notifications.show({
          color: 'green',
          title: t('att.uploaded'),
          message: attachment.filename,
        });
      } catch (error) {
        /*
          ApiUnreachableError, not TypeError. The browser's fetch throws a
          TypeError, but ApiClient catches it and rethrows this -- so a check
          for TypeError never matched, and a request that lost its
          connection mid-flight was reported as a failure instead of queued.
        */
        if (error instanceof ApiUnreachableError) {
          // The request never reached the server: the connection dropped
          // between the check above and the send. Queue it rather than lose it.
          await queueAttachment({
            filename: file.name,
            mimeType: file.type,
            contentBase64,
          });
          setQueuedCount((count) => count + 1);
          continue;
        }
        // A refusal *from* the server is rendered inline below, with its
        // request id. Continuing would upload the rest of a batch while the
        // first failure scrolls away, and queuing something the API has already
        // refused would retry it for ever.
        return;
      }
    }
  }

  function handleReject(rejections: FileRejection[]): void {
    const first = rejections[0];
    if (first === undefined) {
      return;
    }
    const tooLarge = first.errors.some((error) => error.code === 'file-too-large');
    setRejected(
      tooLarge
        ? `"${first.file.name}" er større end 25 MB.`
        : `"${first.file.name}" er ikke en accepteret dokumenttype. Accepteret: ` +
            `${ACCEPTED_MIME_TYPES.join(', ')}.`,
    );
  }

  return (
    <Card withBorder padding="md">
      <Stack gap="sm">
        <Dropzone
          onDrop={(files: FileWithPath[]) => {
            void send(files);
          }}
          onReject={handleReject}
          accept={ACCEPTED_MIME_TYPES}
          maxSize={MAX_ATTACHMENT_BYTES}
          loading={upload.isPending}
          // The label goes on the input the assistive technology actually
          // reaches. A drop target that only announces itself on the wrapper is
          // a control a screen-reader user cannot name.
          inputProps={{ 'aria-label': t('att.chooseFiles') }}
        >
          <Stack gap={4} align="center" mih={110} justify="center" p="md">
            <Text fw={500}>{t('att.dropHere')}</Text>
            <Text size="sm" c="dimmed" ta="center">
              {t('att.dropHelp')}
            </Text>
          </Stack>
        </Dropzone>

        <Group>
          {/*
            The viewfinder, not the system camera directly.

            `<input capture>` still runs on any device without getUserMedia, and
            CameraCapture falls back to exactly that. What the viewfinder adds is
            the confirm step: a photographed receipt is the only evidence of what
            it documents and is kept for five years, and a blurred one is not
            discovered until somebody opens it in an audit. The system camera's
            own preview is gone by the time the file reaches this page.
          */}
          <Button size="lg" onClick={camera.open} disabled={upload.isPending}>
            {t('att.takePhoto')}
          </Button>
        </Group>

        {queuedCount > 0 && (
          <Alert color="blue" variant="light">
            {t('offline.queued', { count: queuedCount })}
          </Alert>
        )}

        {rejected !== null && (
          <Alert color="red" variant="light" title={t('att.rejectedTitle')}>
            {rejected}
          </Alert>
        )}

        <CameraCapture
          opened={cameraOpen}
          onClose={camera.close}
          onCapture={(file) => {
            void send([file]);
          }}
        />

        {upload.isError && (
          <ErrorMessage error={upload.error} title={t('att.uploadFailed')} />
        )}
      </Stack>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// One row
// -----------------------------------------------------------------------------

function AttachmentRow({
  attachment,
  onLink,
  onDelete,
}: {
  readonly attachment: Attachment;
  readonly onLink: () => void;
  readonly onDelete: () => void;
}): ReactNode {
  const t = useT();
  const openAttachment = useOpenAttachment();
  const [fallback, setFallback] = useState<OpenedAttachment | null>(null);
  /*
    Every blob URL this row has handed out.

    A blob URL pins its bytes in memory until it is revoked, so a session spent
    opening documents would otherwise accumulate all of them. Revoked together
    on unmount, which is the first moment neither the opened tab nor the
    fallback link can still need one.
  */
  const [openedUrls, setOpenedUrls] = useState<readonly string[]>([]);
  useEffect(
    () => () => {
      for (const url of openedUrls) {
        URL.revokeObjectURL(url);
      }
    },
    [openedUrls],
  );

  /**
   * Open the file.
   *
   * The bytes come through the API, so this is asynchronous, which is where the
   * user's click stops counting as a gesture and a pop-up blocker steps in. The
   * tab is therefore opened *synchronously*, while the gesture is still live,
   * and pointed at the document when it arrives. If the browser refused the tab
   * anyway, the document is offered as an ordinary link instead.
   *
   * The blob URL is revoked when this row unmounts rather than immediately: the
   * tab needs it to survive the navigation, and revoking it right after
   * assigning `location.href` is a race that shows an empty tab often enough to
   * look intermittent.
   */
  function handleOpen(): void {
    setFallback(null);
    const opened = window.open('', '_blank');
    if (opened !== null) {
      opened.opener = null;
    }
    openAttachment.mutate(attachment.id, {
      onSuccess: (result) => {
        setOpenedUrls((urls) => [...urls, result.url]);
        if (opened === null) {
          setFallback(result);
          return;
        }
        opened.location.href = result.url;
      },
      onError: () => {
        opened?.close();
      },
    });
  }

  return (
    <>
      <Table.Tr>
        <Table.Td>
          <Text>{attachment.filename}</Text>
          <Text size="xs" c="dimmed">
            {attachment.mimeType}
          </Text>
        </Table.Td>
        <Table.Td>
          <Badge color={STATE_COLORS[attachment.state]} variant="light" size="sm">
            {t(STATE_LABELS[attachment.state])}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{formatBytes(attachment.fileSizeBytes)}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{formatDate(attachment.createdAt)}</Text>
        </Table.Td>
        <Table.Td>
          <Group gap="xs" wrap="nowrap" justify="flex-end">
            <Button
              size="compact-sm"
              variant="subtle"
              onClick={handleOpen}
              loading={openAttachment.isPending}
            >
              {t('att.open')}
            </Button>
            {attachment.state === 'unlinked' && (
              <>
                <Button size="compact-sm" variant="subtle" onClick={onLink}>
                  {t('att.link')}
                </Button>
                {/*
                  Offered only while the attachment documents nothing. Once it
                  does, the database refuses deletion and the button would be an
                  affordance for something that cannot happen.
                */}
                <Button
                  size="compact-sm"
                  variant="subtle"
                  color="red"
                  onClick={onDelete}
                >
                  {t('att.delete')}
                </Button>
              </>
            )}
          </Group>
        </Table.Td>
      </Table.Tr>

      {(openAttachment.isError || fallback !== null) && (
        <Table.Tr>
          <Table.Td colSpan={5}>
            {openAttachment.isError && (
              <ErrorMessage error={openAttachment.error} title={t('att.openFailed')} />
            )}
            {fallback !== null && (
              <Alert color="blue" variant="light" title={t('att.openFallbackTitle')}>
                <Stack gap={4}>
                  <Anchor href={fallback.url} target="_blank" rel="noopener noreferrer">
                    {fallback.filename}
                  </Anchor>
                  <Text size="xs" c="dimmed">
                    {t('att.openFallbackHelp')}
                  </Text>
                </Stack>
              </Alert>
            )}
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// Linking
// -----------------------------------------------------------------------------

const linkSchema = z.object({
  entryId: z.uuid('att.link.entryRequired'),
});

type LinkValues = z.infer<typeof linkSchema>;

/**
 * Attach a document to a journal entry.
 *
 * The warning is not decoration. Linking starts the five-year retention, there
 * is no unlink, and the attachment cannot be deleted afterwards — so the one
 * moment to say so is before the button is pressed.
 */
function LinkAttachmentModal({
  attachment,
  opened,
  onClose,
}: {
  readonly attachment: Attachment | null;
  readonly opened: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const t = useT();
  const entries = useJournalEntries();
  const link = useLinkAttachment();

  const form = useForm<LinkValues>({
    mode: 'uncontrolled',
    initialValues: { entryId: '' },
    validate: translating(linkSchema, t, 'att'),
  });

  const entryOptions = (entries.data?.items ?? []).map((entry) => ({
    value: entry.id,
    label: `${formatDate(entry.date)} — ${entry.description}`,
  }));

  function handleSubmit(values: LinkValues): void {
    if (attachment === null) {
      return;
    }
    link.mutate(
      { id: attachment.id, entryId: values.entryId },
      {
        onSuccess: () => {
          notifications.show({
            color: 'green',
            title: t('att.link.linked'),
            message: `${attachment.filename} opbevares nu i fem år.`,
          });
          form.reset();
          onClose();
        },
      },
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        attachment === null
          ? ''
          : t('att.link.title', { filename: attachment.filename })
      }
    >
      {/*
        noValidate: a browser's own constraint validation blocks the submit
        event before Mantine sees it, so the Zod schema never runs and the user
        gets a browser-language tooltip instead of the message written here.
      */}
      <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Alert color="yellow" variant="light" title={t('att.link.irreversibleTitle')}>
            {t('att.link.irreversible')}
          </Alert>

          {entries.isError && <ErrorMessage error={entries.error} />}

          <Select
            label={t('att.link.entry')}
            placeholder={t('att.link.entryPlaceholder')}
            searchable
            data={entryOptions}
            disabled={entries.isPending}
            key={form.key('entryId')}
            {...form.getInputProps('entryId')}
          />

          {link.isError && (
            <ErrorMessage error={link.error} title={t('att.link.failed')} />
          )}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={link.isPending}>
              {t('att.link')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// Deleting
// -----------------------------------------------------------------------------

function DeleteAttachmentModal({
  attachment,
  opened,
  onClose,
}: {
  readonly attachment: Attachment | null;
  readonly opened: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const t = useT();
  const remove = useDeleteAttachment();

  function handleDelete(): void {
    if (attachment === null) {
      return;
    }
    remove.mutate(attachment.id, {
      onSuccess: () => {
        notifications.show({
          color: 'green',
          title: t('att.delete.deleted'),
          message: attachment.filename,
        });
        onClose();
      },
    });
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t('att.delete.title')}>
      <Stack gap="md">
        <Text>
          {attachment === null
            ? ''
            : t('att.delete.body', { filename: attachment.filename })}
        </Text>

        {/*
          The refusal is surfaced rather than assumed away: the state shown may
          be a moment old, and it is the database that decides.
        */}
        {remove.isError && (
          <ErrorMessage error={remove.error} title={t('att.delete.failed')} />
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button color="red" onClick={handleDelete} loading={remove.isPending}>
            {t('att.delete')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
